import { useState } from 'react';
import {
  VStack, Text, Heading, Input, Button, Wrap, WrapItem,
} from '@chakra-ui/react';
import { WizardChip } from '../WizardChip';

const VENUE_TYPES = [
  { id: 'cafe', label: '☕ Café', icon: '☕' },
  { id: 'restaurant', label: '🍽️ Restaurant', icon: '🍽️' },
  { id: 'shop', label: '🛍️ Shop', icon: '🛍️' },
  { id: 'supermarket', label: '🛒 Supermarket', icon: '🛒' },
  { id: 'hotel', label: '🏨 Hotel', icon: '🏨' },
  { id: 'gym', label: '💪 Gym', icon: '💪' },
  { id: 'spa', label: '🧖 Spa', icon: '🧖' },
  { id: 'bar', label: '🍸 Bar', icon: '🍸' },
  { id: 'office', label: '🏢 Office', icon: '🏢' },
  { id: 'clinic', label: '🏥 Clinic', icon: '🏥' },
  { id: 'other', label: '📍 Other', icon: '📍' },
];

export interface LocationStepData {
  name: string;
  venueType: string;
  address?: string;
  organizationName?: string;
  locationName?: string;
  locationType?: string;
}

export interface LocationStepProps {
  onSubmit: (data: { name: string; venueType: string; address?: string }) => void;
  initialData?: { name?: string; venueType?: string; address?: string };
  isLoading?: boolean;
  error?: string | null;
}

export function LocationStep({ onSubmit, initialData, isLoading }: LocationStepProps) {
  const [name, setName] = useState(initialData?.name ?? '');
  const [venueType, setVenueType] = useState(initialData?.venueType ?? '');
  const [address, setAddress] = useState(initialData?.address ?? '');

  const handleSubmit = () => {
    if (name.trim() && venueType) {
      onSubmit({ name: name.trim(), venueType, address: address.trim() || undefined });
    }
  };

  return (
    <VStack flex={1} p={6} gap={6} bg="gray.50" data-testid="wizard-location-step">
      <VStack align="center" gap={2}>
        <Heading as="h2" size="xl" fontWeight="bold" color="gray.800">
          Set up your location
        </Heading>
        <Text fontSize="md" color="gray.500" textAlign="center">
          Tell us about your business location
        </Text>
      </VStack>

      <VStack gap={4} w="100%" maxW="500px" alignSelf="center">
        {/* Location Name */}
        <VStack gap={2} w="100%" align="stretch">
          <Text fontSize="sm" fontWeight="600" color="gray.800">Location name</Text>
          <Input
            size="md"
            placeholder="My Café Downtown"
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="wizard-location-name"
          />
        </VStack>

        {/* Venue Type */}
        <VStack gap={2} w="100%" align="stretch">
          <Text fontSize="sm" fontWeight="600" color="gray.800">Type of venue</Text>
          <Wrap spacing={2}>
            {VENUE_TYPES.map((vt) => (
              <WrapItem key={vt.id}>
                <WizardChip
                  label={vt.label}
                  selected={venueType === vt.id}
                  onPress={() => setVenueType(vt.id)}
                  data-testid={`wizard-venue-${vt.id}`}
                />
              </WrapItem>
            ))}
          </Wrap>
        </VStack>

        {/* Address (optional) */}
        <VStack gap={2} w="100%" align="stretch">
          <Text fontSize="sm" fontWeight="600" color="gray.800">Address (optional)</Text>
          <Input
            size="md"
            placeholder="123 Main Street, City"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            data-testid="wizard-location-address"
          />
        </VStack>

        {/* Submit */}
        <Button
          size="lg"
          bg="brand.500"
          color="white"
          fontWeight="bold"
          w="100%"
          mt={4}
          onClick={handleSubmit}
          isDisabled={!name.trim() || !venueType || isLoading}
          data-testid="wizard-location-submit"
          _hover={{ bg: 'brand.600' }}
        >
          {isLoading ? 'Saving...' : 'Continue'}
        </Button>
      </VStack>
    </VStack>
  );
}

export default LocationStep;
